import { ApiError } from "@ronin/api-client";
import { HealthResponse, routes } from "@ronin/contracts";
import { normalizeServerUrl } from "@ronin/core";
import type { QueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { clearAttachmentCache } from "./attachments.js";
import { clearSharedFiles } from "./files.js";
import { wipeOfflineStore } from "./offline/index.js";
import { serverUrl, setServerUrl } from "./server.js";
import { nativeTokens } from "./tokens.js";

/**
 * spec08 FR8: signs out on the server and forgets everything on the device. The api-client
 * deletes the token even when the server cannot be reached; returns false in that case.
 */
export async function signOutDevice(queryClient: QueryClient): Promise<boolean> {
  let reachedServer = true;
  try {
    await api.auth.signOut();
  } catch {
    reachedServer = false;
  }
  clearAttachmentCache();
  await clearSharedFiles();
  // spec11 FR15: the offline copy, its outbox and its key go too.
  await wipeOfflineStore();
  queryClient.clear();
  return reachedServer;
}

/**
 * spec08 FR5: checks that a Ronin server answers at the address, then switches to it.
 * Switching to another server signs out first.
 */
export async function connectToServer(input: string, queryClient: QueryClient) {
  const url = normalizeServerUrl(input);
  if (!url) throw connectError("Enter an https:// address, for example ronin.example.com.");
  try {
    const res = await fetch(`${url}${routes.health}`, {
      credentials: "omit",
      signal: AbortSignal.timeout(10_000),
    });
    HealthResponse.parse(await res.json());
  } catch {
    throw connectError("Could not reach a Ronin server at this address.");
  }
  if (url !== serverUrl()) {
    if (await nativeTokens.get()) await signOutDevice(queryClient);
    else await wipeOfflineStore();
    setServerUrl(url);
  }
  queryClient.clear();
}

/** The connect form shows the message of an ApiError as it is. */
const connectError = (message: string) => new ApiError(0, "bad_request", message);
